<template>
  <div
    class="vs-component vs-con-table"
    :class="[{ stripe, hoverFlat }, `vs-table-${color}`]"
  >
    <header class="header-table vs-table--header">
      <slot name="header" />
      <div v-if="search" class="con-input-search vs-table--search">
        <input
          v-model="searchText"
          class="input-search vs-table--search-input"
          type="text"
        />
        <vs-icon icon="search" />
      </div>
    </header>
    <div class="con-tablex vs-table--content">
      <div
        class="vs-con-tbody vs-table--tbody"
        :style="tableBodyStyle"
      >
        <table ref="table" class="vs-table vs-table--tbody-table">
          <thead ref="thead" class="vs-table--thead">
            <tr>
              <th v-if="multiple || hasExpandableData" class="td-check">
                <span v-if="multiple" class="con-td-check">
                  <vs-checkbox
                    :key="isPartiallyChecked ? 'remove' : 'check'"
                    :icon="isPartiallyChecked ? 'remove' : 'check'"
                    :model-value="hasSelection"
                    size="small"
                    @change="changeCheckedMultiple"
                  />
                </span>
              </th>
              <slot name="thead" />
            </tr>
          </thead>
          <slot :data="displayData" />
        </table>
        <div v-if="isNoData" class="not-data-table vs-table--not-data">
          {{ noDataText }}
        </div>
        <div
          v-if="pagination"
          class="con-pagination-table vs-table--pagination"
        >
          <vs-pagination
            v-model="current"
            :total="
              searchText && !sst ? totalSearchPages : totalPages
            "
            :description-items="descriptionItems"
            :max-items="currentMaxItems"
            :size-array="queriedResults.length"
            :description="description"
            :description-title="descriptionTitle"
            :description-connector="descriptionConnector"
            :description-body="descriptionBody"
            @change-max-items="changeMaxItems"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VsTable",
  props: {
    modelValue: {
      default: undefined,
    },
    value: {
      default: undefined,
    },
    color: {
      type: String,
      default: "primary",
    },
    noDataText: {
      type: String,
      default: "No data Available",
    },
    stripe: {
      type: Boolean,
      default: false,
    },
    hoverFlat: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: "auto",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    data: {
      default: null,
    },
    notSpacer: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
    maxItems: {
      type: [Number, String],
      default: 5,
    },
    pagination: {
      type: Boolean,
      default: false,
    },
    description: {
      type: Boolean,
      default: false,
    },
    descriptionItems: {
      type: Array,
      default: () => [],
    },
    descriptionTitle: {
      type: String,
      default: undefined,
    },
    descriptionConnector: {
      type: String,
      default: undefined,
    },
    descriptionBody: {
      type: String,
      default: undefined,
    },
    currentPage: {
      type: [Number, String],
      default: 1,
    },
    sst: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    onlyClickCheckbox: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:modelValue",
    "input",
    "selected",
    "dblSelection",
    "change-page",
    "search",
    "sort",
  ],
  provide() {
    return {
      vsTable: this,
    };
  },
  data() {
    return {
      searchText: null,
      current: 1,
      currentMaxItems: 5,
      hasExpandableData: false,
      currentSortKey: null,
      currentSortType: null,
    };
  },
  computed: {
    selectedValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    sourceData() {
      return Array.isArray(this.data) ? this.data : [];
    },
    queriedResults() {
      if (!this.searchText || !this.search || this.sst) {
        return this.sortItems(this.sourceData);
      }
      const search = this.normalize(this.searchText);
      return this.sortItems(this.sourceData).filter((row) =>
        this.normalize(this.getValues(row).toString()).includes(search)
      );
    },
    displayData() {
      if (!this.pagination) return this.queriedResults;
      const max = Math.ceil(this.current * this.currentMaxItems);
      const min = max - this.currentMaxItems;
      return this.queriedResults.slice(min, max);
    },
    totalPages() {
      const length = this.sst && this.total ? this.total : this.sourceData.length;
      return Math.ceil(length / this.currentMaxItems);
    },
    totalSearchPages() {
      return Math.ceil(this.queriedResults.length / this.maxItems);
    },
    isNoData() {
      return this.displayData.length === 0;
    },
    selectedItems() {
      return Array.isArray(this.selectedValue) ? this.selectedValue : [];
    },
    isPartiallyChecked() {
      return this.sourceData.length !== this.selectedItems.length;
    },
    hasSelection() {
      return this.selectedItems.length > 0;
    },
    tableBodyStyle() {
      return {
        maxHeight: this.maxHeight,
        overflow: this.maxHeight !== "auto" ? "auto" : null,
      };
    },
    columnCount() {
      const header = this.$refs.thead;
      return header ? header.querySelectorAll("th").length : 1;
    },
  },
  watch: {
    currentPage(value) {
      this.current = Number(value);
    },
    current(value) {
      if (this.sst) this.$emit("change-page", value);
    },
    maxItems(value) {
      this.currentMaxItems = Number(value);
    },
    searchText(value) {
      if (this.sst) this.$emit("search", value);
      else this.current = 1;
    },
    data() {
      this.$nextTick(this.changeTdsWidth);
    },
  },
  mounted() {
    this.current = Number(this.currentPage);
    this.currentMaxItems = Number(this.maxItems);
    window.addEventListener("resize", this.changeTdsWidth);
    this.$nextTick(this.changeTdsWidth);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.changeTdsWidth);
  },
  methods: {
    registerExpandableRow() {
      this.hasExpandableData = true;
    },
    emitSelection(value) {
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },
    changeCheckedMultiple() {
      const allSelected =
        this.sourceData.length === this.selectedItems.length;
      this.emitSelection(allSelected ? [] : this.sourceData);
    },
    handleCheckbox(row) {
      if (!this.multiple || !this.onlyClickCheckbox) return;
      this.toggleMultipleSelection(row);
    },
    clickRow(row) {
      if (this.onlyClickCheckbox) return;
      if (this.multiple) this.toggleMultipleSelection(row);
      else {
        this.emitSelection(row);
        this.$emit("selected", row);
      }
    },
    toggleMultipleSelection(row) {
      const value = this.selectedItems.slice();
      const index = value.findIndex(
        (item) => JSON.stringify(item) === JSON.stringify(row)
      );
      if (index === -1) value.push(row);
      else value.splice(index, 1);
      this.emitSelection(value);
      this.$emit("selected", row);
    },
    doubleClickRow(row) {
      this.$emit("dblSelection", row);
    },
    sort(key, sortType) {
      this.currentSortKey = key;
      this.currentSortType = sortType;
      if (this.sst) this.$emit("sort", key, sortType);
    },
    sortItems(data) {
      if (!this.currentSortType || !this.currentSortKey) return [...data];
      const direction = this.currentSortType === "desc" ? -1 : 1;
      return [...data].sort((first, second) => {
        if (first[this.currentSortKey] < second[this.currentSortKey]) {
          return -1 * direction;
        }
        if (first[this.currentSortKey] > second[this.currentSortKey]) {
          return direction;
        }
        return 0;
      });
    },
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    getValues(object) {
      const flatten = (value) =>
        Object.values(value || []).reduce(
          (result, item) =>
            typeof item === "object"
              ? result.concat(flatten(item))
              : result.concat(item),
          []
        );
      return flatten(object).filter(
        (item) => typeof item === "string" || typeof item === "number"
      );
    },
    changeMaxItems(index) {
      this.currentMaxItems = Number(this.descriptionItems[index]);
    },
    changeTdsWidth() {
      if (!this.$refs.table) return;
      const firstRow = this.$refs.table.querySelector("tbody .tr-values");
      if (!firstRow) return;
      const cells = firstRow.querySelectorAll(".td");
      const headers = this.$refs.thead
        ? this.$refs.thead.querySelectorAll("th:not(.td-check)")
        : [];
      headers.forEach((header, index) => {
        if (cells[index]) header.style.width = `${cells[index].offsetWidth}px`;
      });
    },
  },
};
</script>
